// client/src/components/parent/ParentChildDashboard.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
  Tooltip,
  Paper,
  TextField,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  LinearProgress,
} from '@mui/material';
import {
  Person as PersonIcon,
  School as SchoolIcon,
  Assignment as AssignmentIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  Link as LinkIcon,
  ChildCare as ChildIcon,
  History as HistoryIcon,
  SentimentSatisfied as MoodIcon,
  Psychology as AnxietyIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import api from '../../services/api';

const ParentChildDashboard = ({ token }) => {
  const navigate = useNavigate();
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // User data for role-based rendering
  const [currentUser, setCurrentUser] = useState(null);

  // Adolescent invitation state
  const [adolescentInvitation, setAdolescentInvitation] = useState(null);
  const [invitationLoading, setInvitationLoading] = useState(false);
  const [invitationError, setInvitationError] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);

  // Check-in history dialog state
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [selectedChild, setSelectedChild] = useState(null);
  const [checkInHistory, setCheckInHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Check if current user is a child (not a parent) - should hide invitation section
  const isChildUser = currentUser?.relatedAccounts?.some(
    link => link.relationshipType === 'child_of' && link.status === 'active'
  );

  const loadChildren = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await api.getLinkedChildren(token);
      setChildren(data.children || []);
    } catch (err) {
      console.error('Error loading children:', err);
      setError(err.message || 'Failed to load linked children');
    } finally {
      setLoading(false);
    }
  };

  const loadInvitations = async () => {
    try {
      const data = await api.getParentInvitations(token);
      // Get the most recent active invitation
      const activeInvitation = data.invitations?.find(inv => inv.status === 'active');
      setAdolescentInvitation(activeInvitation || null);
    } catch (err) {
      console.error('Error loading invitations:', err);
      // Don't show error for invitations - not critical
    }
  };

  const createAdolescentInvitation = async () => {
    setInvitationLoading(true);
    setInvitationError('');
    try {
      const data = await api.createAdolescentInvitation(token);
      setAdolescentInvitation(data.invitation);
    } catch (err) {
      console.error('Error creating invitation:', err);
      setInvitationError(err.message || 'Failed to create invitation');
    } finally {
      setInvitationLoading(false);
    }
  };

  const revokeInvitation = async () => {
    if (!adolescentInvitation || !window.confirm('Are you sure you want to revoke this invitation?')) {
      return;
    }
    setInvitationLoading(true);
    try {
      await api.revokeAdolescentInvitation(adolescentInvitation.id, token);
      setAdolescentInvitation(null);
    } catch (err) {
      console.error('Error revoking invitation:', err);
      setInvitationError(err.message || 'Failed to revoke invitation');
    } finally {
      setInvitationLoading(false);
    }
  };

  const copyToClipboard = async () => {
    if (adolescentInvitation?.invitationUrl) {
      try {
        await navigator.clipboard.writeText(adolescentInvitation.invitationUrl);
        setCopySuccess(true);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  // Load current user data to determine role
  const loadCurrentUser = async () => {
    try {
      const userData = await api.getUser();
      setCurrentUser(userData);
    } catch (err) {
      console.error('Error loading user data:', err);
    }
  };

  useEffect(() => {
    loadChildren();
    loadInvitations();
    loadCurrentUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleSubmitCheckIn = (childId) => {
    navigate(`/parent/child/${childId}/checkin`);
  };

  const handleManageTeachers = (childId) => {
    navigate(`/parent/child/${childId}/teachers`);
  };

  const handleViewCheckIns = async (child) => {
    setSelectedChild(child);
    setHistoryDialogOpen(true);
    setHistoryLoading(true);
    try {
      const data = await api.getChildCheckIns(child._id, token, { limit: 20 });
      setCheckInHistory(data.checkins || []);
    } catch (err) {
      console.error('Error loading check-in history:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleCloseHistoryDialog = () => {
    setHistoryDialogOpen(false);
    setSelectedChild(null);
    setCheckInHistory([]);
  };

  // Helper to render mood/anxiety as visual indicator
  const renderRatingChip = (value, max = 5, type = 'mood') => {
    if (value == null) return null;
    const colors = type === 'mood'
      ? ['#ef4444', '#f97316', '#eab308', '#84cc16', '#22c55e']  // red to green
      : ['#22c55e', '#84cc16', '#eab308', '#f97316', '#ef4444']; // green to red (reversed for anxiety)
    const colorIndex = Math.min(Math.floor((value - 1) / (max / colors.length)), colors.length - 1);
    return (
      <Chip
        size="small"
        label={`${value}/${max}`}
        sx={{
          bgcolor: colors[colorIndex] + '20',
          color: colors[colorIndex],
          fontWeight: 600,
          ml: 1
        }}
      />
    );
  };

  const getPathwayLabel = (pathway) => {
    if (!pathway) return 'Not Set';
    const labels = {
      'adhd-adult': 'ADHD Adult',
      'adhd-adolescent': 'ADHD Adolescent',
      'anxiety': 'Anxiety',
      'depression': 'Depression',
    };
    return labels[pathway] || pathway;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          My Children
        </Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={loadChildren} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {children.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <PersonIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Linked Children
          </Typography>
          <Typography color="text.secondary" mb={2}>
            You don&apos;t have any children linked to your account yet.
            Ask your therapist to help set up the parent-child account link.
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {children.map((child) => (
            <Grid item xs={12} md={6} lg={4} key={child._id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'box-shadow 0.2s',
                  '&:hover': {
                    boxShadow: 4,
                  }
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  {/* Child Name and Status */}
                  <Box display="flex" alignItems="center" mb={2}>
                    <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6" component="h2">
                      {child.name}
                    </Typography>
                  </Box>

                  {/* Pathway */}
                  {child.treatmentPathway && (
                    <Chip
                      label={getPathwayLabel(child.treatmentPathway)}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ mb: 2 }}
                    />
                  )}

                  <Divider sx={{ my: 2 }} />

                  {/* Quick Stats */}
                  <Box>
                    <Box display="flex" alignItems="center" mb={1}>
                      <ScheduleIcon sx={{ fontSize: 18, mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        Last Check-in: {formatDate(child.lastCheckIn?.date)}
                      </Typography>
                    </Box>

                    {/* Show latest check-in values if available */}
                    {child.lastCheckIn && (
                      <Box sx={{ mt: 1, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                          Latest Check-in Summary:
                        </Typography>
                        {child.lastCheckIn.moodRating != null && (
                          <Box display="flex" alignItems="center" mb={0.5}>
                            <MoodIcon sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                              Mood:
                            </Typography>
                            {renderRatingChip(child.lastCheckIn.moodRating, 5, 'mood')}
                          </Box>
                        )}
                        {child.lastCheckIn.anxietyRating != null && (
                          <Box display="flex" alignItems="center" mb={0.5}>
                            <AnxietyIcon sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                              Anxiety:
                            </Typography>
                            {renderRatingChip(child.lastCheckIn.anxietyRating, 5, 'anxiety')}
                          </Box>
                        )}
                        {child.lastCheckIn.sudsRating != null && (
                          <Box display="flex" alignItems="center" mb={0.5}>
                            <Typography variant="body2" color="text.secondary">
                              SUDS:
                            </Typography>
                            {renderRatingChip(child.lastCheckIn.sudsRating, 10, 'anxiety')}
                          </Box>
                        )}
                        {child.lastCheckIn.informantType && child.lastCheckIn.informantType !== 'self' && (
                          <Typography variant="caption" color="primary.main" sx={{ mt: 0.5, display: 'block' }}>
                            Reported by: {child.lastCheckIn.informantType}
                          </Typography>
                        )}
                      </Box>
                    )}

                    {child.pendingTeacherInvitations > 0 && (
                      <Box display="flex" alignItems="center" mb={1} mt={1}>
                        <SchoolIcon sx={{ fontSize: 18, mr: 1, color: 'warning.main' }} />
                        <Typography variant="body2" color="warning.main">
                          {child.pendingTeacherInvitations} pending teacher invitation(s)
                        </Typography>
                      </Box>
                    )}

                    {child.completedTeacherAssessments > 0 && (
                      <Box display="flex" alignItems="center" mb={1}>
                        <CheckCircleIcon sx={{ fontSize: 18, mr: 1, color: 'success.main' }} />
                        <Typography variant="body2" color="success.main">
                          {child.completedTeacherAssessments} teacher assessment(s) completed
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </CardContent>

                <Divider />

                <CardActions sx={{ justifyContent: 'space-between', p: 2, flexWrap: 'wrap', gap: 1 }}>
                  <Button
                    size="small"
                    startIcon={<AssignmentIcon />}
                    onClick={() => handleSubmitCheckIn(child._id)}
                    variant="contained"
                    color="primary"
                  >
                    Submit Check-In
                  </Button>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      size="small"
                      startIcon={<HistoryIcon />}
                      onClick={() => handleViewCheckIns(child)}
                      variant="outlined"
                      color="secondary"
                    >
                      History
                    </Button>
                    <Button
                      size="small"
                      startIcon={<SchoolIcon />}
                      onClick={() => handleManageTeachers(child._id)}
                      variant="outlined"
                    >
                      Teachers
                    </Button>
                  </Box>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Adolescent Invitation Section - Only show for parents, not for children */}
      {!isChildUser && (
      <Card sx={{ mt: 4 }}>
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            <ChildIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">
              Invite Your Adolescent
            </Typography>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Create a signup link for your teen (ages 12-17) to create their own AIMHI account.
            Their account will automatically be linked to yours, allowing you to submit check-ins
            and track their progress.
          </Typography>

          {invitationError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {invitationError}
            </Alert>
          )}

          {!adolescentInvitation ? (
            <Button
              variant="contained"
              startIcon={invitationLoading ? <CircularProgress size={20} color="inherit" /> : <AddIcon />}
              onClick={createAdolescentInvitation}
              disabled={invitationLoading}
            >
              {invitationLoading ? 'Creating...' : 'Create Invitation Link'}
            </Button>
          ) : (
            <Box>
              {/* Invitation Status */}
              <Box display="flex" alignItems="center" mb={2}>
                <LinkIcon sx={{ mr: 1, color: 'success.main' }} />
                <Typography variant="body2" color="success.main" fontWeight="medium">
                  Invitation link ready to share
                </Typography>
                {adolescentInvitation.linkedChild && (
                  <Chip
                    label={`Used by ${adolescentInvitation.linkedChild.name}`}
                    size="small"
                    color="success"
                    sx={{ ml: 2 }}
                  />
                )}
              </Box>

              {/* Invitation URL */}
              <TextField
                fullWidth
                value={adolescentInvitation.invitationUrl || ''}
                InputProps={{
                  readOnly: true,
                  sx: { fontFamily: 'monospace', fontSize: '0.875rem' }
                }}
                size="small"
                sx={{ mb: 2 }}
              />

              {/* Action Buttons */}
              <Box display="flex" gap={1} flexWrap="wrap">
                <Button
                  variant="outlined"
                  startIcon={<CopyIcon />}
                  onClick={copyToClipboard}
                  size="small"
                >
                  Copy Link
                </Button>
                {!adolescentInvitation.linkedChild && (
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={invitationLoading ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
                    onClick={revokeInvitation}
                    disabled={invitationLoading}
                    size="small"
                  >
                    Revoke
                  </Button>
                )}
              </Box>

              {/* Invitation Details */}
              <Box mt={2} p={2} bgcolor="grey.50" borderRadius={1}>
                <Typography variant="caption" color="text.secondary" component="div">
                  <strong>Code:</strong> {adolescentInvitation.signupCode}
                </Typography>
                <Typography variant="caption" color="text.secondary" component="div">
                  <strong>Created:</strong> {new Date(adolescentInvitation.createdAt).toLocaleDateString()}
                </Typography>
                <Typography variant="caption" color="text.secondary" component="div">
                  <strong>Expires:</strong> {new Date(adolescentInvitation.expiresAt).toLocaleDateString()}
                </Typography>
                <Typography variant="caption" color="text.secondary" component="div">
                  <strong>Status:</strong> {adolescentInvitation.status}
                </Typography>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>
      )}

      {/* Copy Success Snackbar */}
      <Snackbar
        open={copySuccess}
        autoHideDuration={3000}
        onClose={() => setCopySuccess(false)}
        message="Link copied to clipboard!"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      {/* Check-in History Dialog */}
      <Dialog
        open={historyDialogOpen}
        onClose={handleCloseHistoryDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              Check-in History: {selectedChild?.name}
            </Typography>
            <IconButton onClick={handleCloseHistoryDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {historyLoading ? (
            <Box sx={{ py: 4 }}>
              <LinearProgress />
              <Typography align="center" sx={{ mt: 2 }} color="text.secondary">
                Loading check-in history...
              </Typography>
            </Box>
          ) : checkInHistory.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                No check-ins recorded yet.
              </Typography>
            </Box>
          ) : (
            <List>
              {checkInHistory.map((checkIn, index) => (
                <React.Fragment key={checkIn._id || index}>
                  <ListItem sx={{ flexDirection: 'column', alignItems: 'flex-start', py: 2 }}>
                    <Box display="flex" justifyContent="space-between" width="100%" mb={1}>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {formatDate(checkIn.date)}
                        {checkIn.informantType && checkIn.informantType !== 'self' && (
                          <Chip
                            size="small"
                            label={`by ${checkIn.informantType}`}
                            sx={{ ml: 1 }}
                            color="info"
                            variant="outlined"
                          />
                        )}
                      </Typography>
                      {checkIn.templateName && (
                        <Chip size="small" label={checkIn.templateName} variant="outlined" />
                      )}
                    </Box>
                    <Box display="flex" gap={2} flexWrap="wrap">
                      {checkIn.moodRating != null && (
                        <Box display="flex" alignItems="center">
                          <MoodIcon sx={{ fontSize: 18, mr: 0.5, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            Mood:
                          </Typography>
                          {renderRatingChip(checkIn.moodRating, 5, 'mood')}
                        </Box>
                      )}
                      {checkIn.anxietyRating != null && (
                        <Box display="flex" alignItems="center">
                          <AnxietyIcon sx={{ fontSize: 18, mr: 0.5, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            Anxiety:
                          </Typography>
                          {renderRatingChip(checkIn.anxietyRating, 5, 'anxiety')}
                        </Box>
                      )}
                      {checkIn.sudsRating != null && (
                        <Box display="flex" alignItems="center">
                          <Typography variant="body2" color="text.secondary">
                            SUDS:
                          </Typography>
                          {renderRatingChip(checkIn.sudsRating, 10, 'anxiety')}
                        </Box>
                      )}
                    </Box>
                    {checkIn.notes && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                        &ldquo;{checkIn.notes}&rdquo;
                      </Typography>
                    )}
                  </ListItem>
                  {index < checkInHistory.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseHistoryDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ParentChildDashboard;
